import { AxiosResponse } from 'axios';
import { addDays } from 'date-fns';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import slugify from 'slugify';
import { v4 as uuidv4 } from 'uuid';

import { GymStatus } from '@tgg/common-types';
import { axiosInstance } from '@tgg/micro-services/axios';
import {
    Class,
    GymDisplayNamesResponse,
    GymDto,
    SessionsSearchResponseBody,
} from '@tgg/services';
import {
    ButtonBase,
    DownloadApp,
    HorizontalRule,
    ImageStandalone,
    Layout,
    TimetableClass,
} from '@tgg/ui';
import {
    GsspWithLoggerParameters,
    UnCapitalizedProperties,
    getFormattedDate,
    gsspWithLogger,
} from '@tgg/util';

import { applicationIdentifier } from 'apps/commerce/core/constants';
import {
    androidAppLink,
    appleAppLink,
} from 'apps/commerce/core/constants/appLinks';
import {
    PublicRuntimeConfig,
    ServerRuntimeConfig,
} from 'apps/commerce/next.config.types';

import {
    checkClassAvailability,
    useGetTabsContent,
} from './ClassTimetable.helpers';
import {
    StyledAnchor,
    StyledButtonWrapper,
    StyledMonthHeading,
    StyledNoClassesParagraph,
    StyledParagraph,
    StyledTabs,
    StyledWrapper,
} from './ClassTimetable.styled';
import { ClassTimetableProperties } from './ClassTimetable.types';

const {
    serverRuntimeConfig: { APP_INTERNAL_BASE_URL },
    publicRuntimeConfig: { APP_BASE_PATH },
}: {
    serverRuntimeConfig: ServerRuntimeConfig;
    publicRuntimeConfig: PublicRuntimeConfig;
} = getConfig();

export const ClassTimetable = ({
    isMobileRequest,
    branchName,
    startDate,
    endDate,
    classes,
    branchPath,
}: ClassTimetableProperties) => {
    const router = useRouter();

    const { tabsData } = useGetTabsContent({
        classes,
        startDate: new Date(startDate),
        endDate: new Date(endDate),
    });

    // 3 arrays needed for Tabs component
    const tabsDatesArray = tabsData.map(t => t.name);
    const tabsContentArrays = tabsData.map(t => t.data);

    const classesCards = tabsContentArrays.map(tabsContentArray => {
        const mappedContentArray = tabsContentArray.map((cl: Class) => {
            const availability = checkClassAvailability(
                cl.capacityBooked,
                cl.bookedMax,
            );
            return (
                <TimetableClass
                    key={cl.classId}
                    className={cl.name}
                    startTime={cl.startTime.toString()}
                    duration={cl.durationMinutes}
                    trainer={cl.instructorName}
                    availability={availability}
                    description={cl.description}
                    placesRation={`${cl.capacityBooked}/${cl.bookedMax}`}
                />
            );
        });

        return mappedContentArray.length > 0
            ? mappedContentArray
            : [
                  <StyledNoClassesParagraph key={uuidv4()}>
                      no available classes for this date
                  </StyledNoClassesParagraph>,
              ];
    });

    const currentMonth = new Date(startDate).toLocaleString('default', {
        month: 'long',
    });

    return (
        <Layout
            isMobileSession={isMobileRequest}
            title={`${branchName} classes timetable`}
        >
            <ImageStandalone
                image={{
                    name: 'The_Gym_Group_Asset-Generic-Members_Doing_Warrior_pose_At_Yoga_Pilates',
                    endpoint: 'thegymgroup',
                    defaultHost: 'cdn.media.amplience.net',
                }}
                alt="The Gym Group asset-generic-members doing warrior pose at yoga pilates"
                prioritiseImageLoading={false}
                lazy={false}
            />
            <StyledWrapper>
                <StyledParagraph data-testid="branchClassesParagraph">
                    Take a look at our classes below. Ready to book? Download
                    the app on{' '}
                    <StyledAnchor href={appleAppLink}>
                        <strong>iOS</strong>
                    </StyledAnchor>{' '}
                    or{' '}
                    <StyledAnchor href={androidAppLink}>
                        <strong>Android</strong>
                    </StyledAnchor>{' '}
                    to book and manage your classes, up to 7 days in advance.
                </StyledParagraph>
                <HorizontalRule />
                <StyledMonthHeading
                    alignOnDesktop="center"
                    variant="h2"
                    gutterBottom={false}
                >
                    {currentMonth.toLowerCase()}
                </StyledMonthHeading>
                <StyledTabs
                    isDaysOfTheWeek
                    tabGroupAriaLabel="Classes timetable"
                    uniqueId={branchName}
                    isCustomDays
                    customDays={tabsDatesArray}
                    daysOfTheWeekContent={[...classesCards] as any}
                />
                {branchPath !== null && (
                    <StyledButtonWrapper>
                        <ButtonBase
                            text="join now"
                            data-testid="branchClasses-btn"
                            fullWidth
                            onClick={() => router.push(branchPath)}
                        />
                    </StyledButtonWrapper>
                )}
                <DownloadApp customSubTitle="book your class now" />
            </StyledWrapper>
        </Layout>
    );
};

const pageParameters: GsspWithLoggerParameters = {
    applicationIdentifier,
    meta: {
        journeyInfo: {
            journeyName: 'Classes',
            journeyStep: 'ClassTimetable',
        },
    },
};

export const getServerSideProps = gsspWithLogger<ClassTimetableProperties>(
    async (_context, injectorParameters) => {
        const { isMobileRequest, loggerParameters, logger } =
            injectorParameters;
        const { req, params } = _context;

        const branchName = params?.branchName as string;
        const branchNameWithoutSuffix = branchName.replace(/-classes$/, '');

        const domain = `${APP_INTERNAL_BASE_URL}${APP_BASE_PATH}`;

        const gymDisplayNamesProxyRouteUrl = `${domain}/api/proxy/gyms/displaynames/`;

        logger.debug(
            `ClassTimetable getServerSidePropertiesForBranchName - about to call ${gymDisplayNamesProxyRouteUrl} to get gyms`,
            loggerParameters,
        );

        const {
            data: gymsWithBranchIdAndDisplayName,
        }: AxiosResponse<GymDisplayNamesResponse[]> = await axiosInstance.get(
            gymDisplayNamesProxyRouteUrl,
            {
                params: {
                    statuses: [
                        GymStatus.Open,
                        GymStatus.ClosedForMaintenance,
                        GymStatus.ComingSoon,
                        GymStatus.Closed,
                    ],
                },
            },
        );

        const foundGym = gymsWithBranchIdAndDisplayName.find(gym => {
            return (
                slugify(gym.displayName, { lower: true }) ===
                branchNameWithoutSuffix
            );
        });

        const { branchId } = foundGym || {};

        if (!branchId) {
            throw new Error(
                `Cound not find a branchId for ${branchNameWithoutSuffix} branch name`,
            );
        }

        const gymProxyRouteUrl = `${domain}/api/proxy/gyms/${branchId}`;

        logger.debug(
            `ClassTimetable getServerSideProps - about to call ${gymProxyRouteUrl} to get gyms`,
            loggerParameters,
        );
        const { data: gym }: AxiosResponse<GymDto> = await axiosInstance.get<
            any,
            AxiosResponse<GymDto>
        >(gymProxyRouteUrl);

        const startDate = new Date();
        const endDate = addDays(startDate, 6);
        const endDateAPI = addDays(startDate, 7);
        const formattedStartDate = getFormattedDate(startDate, 'yyyy-MM-dd');
        const formattedEndDate = getFormattedDate(endDate, 'yyyy-MM-dd');
        const formattedEndDateAPI = getFormattedDate(endDateAPI, 'yyyy-MM-dd');

        const formattedPath = gym.website?.replace(
            'https://www.thegymgroup.com',
            '',
        );

        const sessionsSearchPath = `${domain}/api/proxy/sessions-search/`;

        const { data } = await axiosInstance.get<
            SessionsSearchResponseBody,
            AxiosResponse<UnCapitalizedProperties<SessionsSearchResponseBody>>
        >(sessionsSearchPath, {
            params: {
                branchId: gym.branchId,
                startDate: formattedStartDate,
                endDate: formattedEndDateAPI,
            },
        });

        return {
            props: {
                branchPath: formattedPath ?? null,
                branchName: gym.name,
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                classes: data.data.sessions,
                isMobileRequest,
            },
        };
    },
    pageParameters,
);
